
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import Box from 'carbon-react/lib/components/box';
import Button from 'carbon-react/lib/components/button';
import FileInput, { FileUploadStatusProps } from 'carbon-react/lib/components/file-input';
import { LoaderSpinner } from 'carbon-react/lib/components/loader-spinner';
import Typography from 'carbon-react/lib/components/typography';

import styles from './excel-upload-panel.scss';

export interface ExcelUploadPanelProps {
  error?: string;
  isLoading?: boolean;
  onChange: (files: FileList) => void;
  templateLink?: string;
  uploadStatus?: FileUploadStatusProps;
}

export const ExcelUploadPanel = ({ error, isLoading, onChange, templateLink, uploadStatus }: ExcelUploadPanelProps) => {
  const intl = useIntl();

  const mode = (() => {
    if (isLoading) {
      return 'loading';
    }
    if (!templateLink) {
      return 'no-template';
    } else {
      return 'ready';
    }
  })();

  return (
    <Box
      className={styles.container}
      backgroundColor="--colorsUtilityMajor010"
      borderRadius={'borderRadius200'}
      padding={3}
    >
      <Typography variant="h5" fontWeight="500" mb={2}>
        <FormattedMessage
          id="predict.create_forecast.upload_excel_spreadsheet_v2"
          defaultMessage="Upload Excel spreadsheet"
        />
      </Typography>

      {mode === 'loading' && (
        <LoaderSpinner
          m={3}
          mb={0}
          size="small"
          spinnerLabel={intl.formatMessage({
            id: 'common.loading_template',
            defaultMessage: 'Loading template...'
          })}
        />
      )}

      {mode === 'no-template' && (
        <Typography variant="p" lineHeight="150%" mb={1} color="--colorsUtilityYin055">
          <FormattedMessage
            id="predict.create_forecast.complete_details_for_excel_template"
            defaultMessage="To get started, please complete the required forecast details"
          />
        </Typography>
      )}

      {mode === 'ready' && (
        <>
          <Typography variant="p" fontWeight="500" lineHeight="150%" mb={0}>
            <FormattedMessage id="predict.create_forecast.step_x" defaultMessage="Step {step}" values={{ step: 1 }} />
          </Typography>
          <Typography variant="p" lineHeight="150%" mb={1} color="--colorsUtilityYin055">
            <FormattedMessage
              id="predict.create_forecast.download_the_excel_template_below"
              defaultMessage="Download the Excel template below"
            />
          </Typography>
          <Button
            buttonType="secondary"
            iconType="download"
            iconPosition="after"
            href={templateLink}
            target="_blank"
            rel="noopener noreferrer"
            mb={2}
          >
            <FormattedMessage id="predict.create_forecast.download_template" defaultMessage="Download template" />
          </Button>
          <Typography variant="p" fontWeight="500" lineHeight="150%" mb={0}>
            <FormattedMessage id="predict.create_forecast.step_x" defaultMessage="Step {step}" values={{ step: 2 }} />
          </Typography>
          <Typography variant="p" lineHeight="150%" mb={2} color="--colorsUtilityYin055">
            <FormattedMessage
              id="predict.create_forecast.enter_p_l_data"
              defaultMessage="Enter P&L data, following the {guidelinesLink}. Note: figures entered must be monthly."
              values={{
                guidelinesLink: (
                  <a href="about:blank" target="_blank" rel="noreferrer">
                    <FormattedMessage
                      id="predict.create_forecast.import_guidelines_link"
                      defaultMessage="import guidelines (new tab)"
                    />
                  </a>
                )
              }}
            />
          </Typography>

          <FileInput
            label={intl.formatMessage(
              { id: 'predict.create_forecast.step_x', defaultMessage: 'Step {step}' },
              { step: 3 }
            )}
            inputHint={intl.formatMessage({
              id: 'predict.create_forecast.upload_file_below_max_file_size_is',
              defaultMessage: 'Upload file below. Max file size is 25mb'
            })}
            buttonText={intl.formatMessage({
              id: 'common.select_file',
              defaultMessage: 'Select file'
            })}
            dragAndDropText={intl.formatMessage({
              id: 'common.or_drag_and_drop_your_file',
              defaultMessage: 'or drag and drop your file'
            })}
            onChange={onChange}
            required
            mb={0}
            uploadStatus={uploadStatus}
            accept=".xls, .xlsx"
            error={error}
          />
        </>
      )}
    </Box>
  );
};
